import React, { useCallback, useRef } from 'react';
import YouTubeShortCard from '@/components/YouTubeShortCard';
import { useDeviceFeatures } from '@/hooks/useResponsive';
import { sendYouTubeCommand } from '@/utils/youtube';

interface ActivityVideoGalleryProps {
  videoIds: string[];
  sectionTitle: string;
  activityLabel: string;
}

const ActivityVideoGallery: React.FC<ActivityVideoGalleryProps> = ({
  videoIds,
  sectionTitle,
  activityLabel,
}) => {
  const { isMobile } = useDeviceFeatures();
  const iframeRefs = useRef<Map<number, HTMLIFrameElement>>(new Map());

  const registerIframe = useCallback((index: number, iframe: HTMLIFrameElement | null) => {
    if (iframe) {
      iframeRefs.current.set(index, iframe);
    } else {
      iframeRefs.current.delete(index);
    }
  }, []);

  const pauseAllExcept = useCallback((activeIndex: number) => {
    iframeRefs.current.forEach((iframe, index) => {
      if (index !== activeIndex) {
        sendYouTubeCommand(iframe, 'pauseVideo');
      }
    });
  }, []);

  const handleRequestPlay = useCallback(
    (index: number) => {
      pauseAllExcept(index);
      const iframe = iframeRefs.current.get(index);
      if (iframe) {
        sendYouTubeCommand(iframe, 'playVideo');
      }
    },
    [pauseAllExcept]
  );

  const handleRequestPause = useCallback((index: number) => {
    const iframe = iframeRefs.current.get(index);
    sendYouTubeCommand(iframe ?? null, 'pauseVideo');
  }, []);

  if (videoIds.length === 0) {
    return null;
  }

  const gridClass =
    videoIds.length === 1
      ? 'grid grid-cols-1 gap-8 justify-items-center max-w-sm mx-auto'
      : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center';

  return (
    <section className="max-w-5xl mx-auto mt-10 mb-10">
      <h2 className="text-2xl md:text-3xl font-semibold text-[#1C3F75] text-center mb-6">
        {sectionTitle}
      </h2>
      <div className={gridClass}>
        {videoIds.map((videoId, index) => (
          <YouTubeShortCard
            key={`${videoId}-${index}`}
            videoId={videoId}
            activityLabel={activityLabel}
            cardIndex={index}
            isMobile={isMobile}
            onRegisterIframe={registerIframe}
            onRequestPlay={handleRequestPlay}
            onRequestPause={handleRequestPause}
          />
        ))}
      </div>
    </section>
  );
};

export default ActivityVideoGallery;
